import { useParams } from "react-router";
import { meldungsIdAnzeige } from "../design/Kennung";
import { Symbol } from "../design/Symbol";
import { Button } from "react-aria-components";
import { navigieren } from "./geraeteposition";
import { useEigenesRevier, useRevierSammlung, type Rahmen } from "./lage";

/**
 * Karte der Leitung (oberflaeche.md §3.2, §6 „Karte“; F-150): offene Anschüsse als `button` „Anschuss <Meldungs-Id>“ mit
 * dem Fluchtrichtungspfeil, dessen Richtung als Text im Element steht. Die Fläche zeigt die Anschüsse relativ
 * zueinander (Nord oben); ohne Anschussposition steht ein Anschuss in der Mitte. Ein Fingertipp übergibt den Anschuss
 * an die Karten-App. Weitere Lagen der Karte (Stände, Notrufe, vermisste Hunde) ergänzt der Kontext Lage.
 */
type Position = { Breite: number; Länge: number };

function lageAufDerFlaeche(p: Position | null, alle: Position[]): { links: number; oben: number } {
  if (!p || alle.length < 2) return { links: 50, oben: 50 };
  const breiten = alle.map((x) => x.Breite);
  const laengen = alle.map((x) => x.Länge);
  const spanne = (min: number, max: number) => (max - min) || 1;
  const rand = 12;
  return {
    links: rand + ((p.Länge - Math.min(...laengen)) / spanne(Math.min(...laengen), Math.max(...laengen))) * (100 - 2 * rand),
    oben: rand + ((Math.max(...breiten) - p.Breite) / spanne(Math.min(...breiten), Math.max(...breiten))) * (100 - 2 * rand),
  };
}

export function KarteDerLeitung({ rahmen }: { rahmen: Rahmen }) {
  const { id = "" } = useParams();
  const revier = useEigenesRevier(id);
  const anschuesse = useRevierSammlung(id, revier, "offene_suchen").filter((s) => s["Suchart"] === "Nachsuche");
  const positionen = anschuesse.map((a) => a["Anschussposition"] as Position | null).filter((p): p is Position => !!p);
  return rahmen({
    drueckjagdId: id, name: "Karte", flaeche: true,
    children: (
      <section className="karte-gross platte karte-leitung" role="region" aria-label="Karte">
        {anschuesse.map((a) => {
          const p = a["Anschussposition"] as Position | null;
          const { links, oben } = lageAufDerFlaeche(p, positionen);
          const richtung = a["Fluchtrichtung"] as string | null;
          return (
            <Button key={a._id} className="karte__anschuss" aria-labelledby={`k-${a._id}`} aria-describedby={richtung ? `kp-${a._id}` : undefined}
              style={{ left: `${links}%`, top: `${oben}%` }} onPress={() => navigieren(p)}>
              <Symbol name="anschuss" />
              <span className="karte__bez" id={`k-${a._id}`}>Anschuss <span className="kennung">{meldungsIdAnzeige(a["Schuss"])}</span></span>
              {richtung ? <span className={`karte__pfeil k--richtung-${richtung}`} id={`kp-${a._id}`}><Symbol name="fluchtrichtung" />{richtung}</span> : null}
            </Button>
          );
        })}
        {anschuesse.length === 0 ? <div className="leer"><Symbol name="karte" /><span>Keine offenen Anschüsse</span></div> : null}
      </section>
    ),
  });
}
